import { useContext, useEffect, useRef, useState } from "react";
import { AppContext } from "../../App";
import PopUp from "../PopUp";
import api from "../api/api";
import functions from "../../functions/functions";
import LoadIcon from "../../icons/LoadIcon";
import { paymentErrorHint, paymentErrorSummary } from "../../functions/paymentErrors";

export const DOJO_PAYMENT_METHOD_ID = 23;

const DOJO_POLL_INTERVAL_MS = 1000;

const DOJO_NOTIFICATION_TEXTS = {
	PresentCard: "Presentare la carta sul terminale",
	InsertCard: "Inserire la carta",
	EnterPin: "Il cliente sta digitando il PIN",
	RemoveCard: "Rimuovere la carta",
	PleaseWait: "Attendere...",
};

const DOJO_PAID_STATUSES = ["Captured", "Authorized", "SignatureVerificationAccepted"];

const DOJO_NOT_CANCELLABLE_TEXT = "La carta è già stata presentata: non è più possibile annullare";

function dojoStatusText(session) {
	if (!session) return "Avvio del pagamento sul terminale...";
	if (session.status == "CancelRequested") return "Annullamento in corso...";
	if (DOJO_PAID_STATUSES.includes(session.status)) return "Pagamento autorizzato, conferma dell'ordine in corso...";
	return DOJO_NOTIFICATION_TEXTS[session.notification] || "Pagamento in corso sul terminale";
}

function DojoPaymentPopup({ order, edit }) {
	const { setPosPaymentPopup, setLoading, setConfirm, init_cart, config, loadNextSplittedOrder } = useContext(AppContext);
	const [session, setSession] = useState(null);
	const [errorInfo, setErrorInfo] = useState(null);
	const [notice, setNotice] = useState(null);
	const pollInterval = useRef(null);
	const pollInFlight = useRef(false);
	const closed = useRef(false);

	useEffect(() => {
		start();
		return () => {
			closed.current = true;
			stopPolling();
		};
	}, []);

	const stopPolling = () => {
		clearInterval(pollInterval.current);
		pollInterval.current = null;
	};

	const startPolling = () => {
		stopPolling();
		pollInterval.current = window.setInterval(() => poll(), DOJO_POLL_INTERVAL_MS);
	};

	const close = () => {
		if (closed.current) return;
		closed.current = true;
		stopPolling();
		init_cart();
		loadNextSplittedOrder();
		setPosPaymentPopup(false);
		window.parent.postMessage("close", "*");
	};

	const showError = (code, message) => {
		stopPolling();
		setErrorInfo({ code: code || null, message: message || null });
	};

	const handleSession = (data) => {
		if (!data || closed.current) return;
		setSession(data);
		if (data.final) {
			if (!data.error_code) close();
			else showError(data.error_code, data.error_message);
		}
	};

	const start = async () => {
		setSession(null);
		setNotice(null);
		setErrorInfo(null);
		if (!config.dojo_terminal_id) {
			showError("DOJO_NO_TERMINAL", null);
			return;
		}
		const res = await api.postProtected("/dojo/start_session/", {
			order_id: order.id,
			terminal_id: config.dojo_terminal_id,
		});
		if (closed.current) return;
		if (res.success == 1) {
			handleSession(res.data);
			if (!res.data?.final) startPolling();
		} else showError(res.error_code || "UNKNOWN", res.error);
	};

	const poll = async () => {
		if (pollInFlight.current) return;
		pollInFlight.current = true;
		try {
			const res = await api.postProtected("/dojo/status/", { order_id: order.id });
			if (res.success == 1) handleSession(res.data);
		} finally {
			pollInFlight.current = false;
		}
	};

	const cancelSession = () => {
		setConfirm({
			title: "Attenzione",
			message: "Sei sicuro di annullare il pagamento sul terminale?",
			onConfirm: async () => {
				setLoading(true);
				const res = await api.postProtected("/dojo/cancel/", { order_id: order.id });
				setLoading(false);
				if (res.success == 1) handleSession(res.data);
				else if (res.error_code == "DOJO_NOT_CANCELLABLE") setNotice(DOJO_NOT_CANCELLABLE_TEXT);
				else
					setConfirm({
						title: "Errore",
						message: res.error,
					});
			},
		});
	};

	const sendSignature = async (accepted) => {
		setLoading(true);
		const res = await api.postProtected("/dojo/signature/", { order_id: order.id, accepted });
		setLoading(false);
		if (res.success == 1) handleSession(res.data);
		else
			setConfirm({
				title: "Errore",
				message: res.error,
			});
	};

	const markPaid = () => {
		setConfirm({
			title: "Registra come pagato",
			message:
				"Confermi di aver verificato sullo schermo o sullo scontrino del terminale Dojo che il pagamento è andato a buon fine? L'ordine verrà registrato come pagato.",
			onConfirm: async () => {
				setLoading(true);
				const res = await api.postProtected("/dojo/mark_paid/", { order_id: order.id });
				setLoading(false);
				if (res.success == 1) close();
				else
					setConfirm({
						title: "Errore",
						message: res.error,
					});
			},
		});
	};

	const retry = async () => {
		setLoading(true);
		const res = await api.postProtected("/orders/edit/" + order.id + "/", {
			status: "awaiting_payment",
		});
		setLoading(false);
		if (res.success == 1) start();
		else
			setConfirm({
				title: "Errore",
				message: res.error,
			});
	};

	const cancelPayment = () => {
		setConfirm({
			title: "Attenzione",
			message: "Sei sicuro di annullare il pagamento?",
			onConfirm: async () => {
				if (!edit) {
					setLoading(true);
					const res = await api.postProtected("/orders/edit/" + order.id + "/", {
						status: "archived",
					});
					setLoading(false);
					if (res.success == 1) {
						setPosPaymentPopup(false);
					} else
						setConfirm({
							title: "Errore",
							message: res.error,
						});
				} else setPosPaymentPopup(false);
			},
		});
	};

	const getAmount = () => {
		const dojoPayments = (order.payments || []).filter((p) => p.payment_method_id == DOJO_PAYMENT_METHOD_ID);
		if (dojoPayments.length == 0) return order.total;
		return dojoPayments.reduce((total, p) => total + parseFloat(p.amount || 0), 0);
	};

	const expired = errorInfo?.code == "DOJO_EXPIRED";
	const signatureRequired = !errorInfo && session?.signature_required && !session?.final;

	return (
		<PopUp
			customClass={errorInfo ? "electronic-payment electronic-payment-error" : "electronic-payment"}
			size=""
			options={{
				onClose: errorInfo ? () => setPosPaymentPopup(false) : false,
				title: "Pagamento Elettronico Dojo",
			}}
		>
			{errorInfo ? (
				<div className="inner-content error-retry">
					<h3>Errore</h3>
					<h1>{expired ? "Esito non confermato" : "Errore pagamento"}</h1>
					{expired ? (
						<h5 className="payment-error-hint">
							Non è stato possibile confermare l'esito del pagamento. Controlla lo schermo o lo
							scontrino del terminale Dojo.
						</h5>
					) : (
						<>
							<h3 className="payment-error-code">
								{paymentErrorSummary({
									payment_error_code: errorInfo.code,
									payment_error_message: errorInfo.message,
								})}
							</h3>
							{paymentErrorHint(errorInfo.code) && (
								<h5 className="payment-error-hint">{paymentErrorHint(errorInfo.code)}</h5>
							)}
						</>
					)}
					{errorInfo.code && <h5 className="payment-error-technical-code">Codice: {errorInfo.code}</h5>}
					{expired && <button onClick={markPaid}>Registra come pagato</button>}
					<button onClick={retry}>Riprova</button>
					<button className="payment-error-cancel" onClick={cancelPayment}>
						Annulla pagamento
					</button>
				</div>
			) : signatureRequired ? (
				<div className="inner-content">
					<h3>Verifica firma</h3>
					<h5>Confrontare la firma sullo scontrino del terminale con quella sulla carta.</h5>
					<button onClick={() => sendSignature(true)}>Firma valida</button>
					<button onClick={() => sendSignature(false)}>Firma non valida</button>
				</div>
			) : (
				<div className="inner-content">
					<span>
						<LoadIcon></LoadIcon>
					</span>
					<h3>{dojoStatusText(session)}</h3>
					<div className="total-import">
						<h3>Importo totale:</h3> <h1>{functions.formatter.format(getAmount())}</h1>
					</div>
					{notice && (
						<h5>
							<strong>{notice}</strong>
						</h5>
					)}
					{session && !DOJO_PAID_STATUSES.includes(session.status) && session.status != "CancelRequested" && (
						<button onClick={cancelSession}>Annulla</button>
					)}
				</div>
			)}
		</PopUp>
	);
}

export default DojoPaymentPopup;
